<div class="assist">

  <!-- ── Header ─────────────────────────────────────────────────
       Pas de fil d'Ariane ici : la coquille affiche déjà le sien, tiré de
       `data.breadcrumb` de la route — le composant le répétait juste dessous. -->

  <h1 class="assist__title">{{ 'ASSISTANCE.TITLE' | translate }}</h1>
  <p class="assist__subtitle">
    {{ (audience() === 'Admin' ? 'ASSISTANCE.STAFF_SUBTITLE' : 'ASSISTANCE.SUBTITLE') | translate }}
  </p>

  <!-- ── Équipe : le public regardé ───────────────────────────── -->
  <div class="viewas" *ngIf="isStaff">
    <span>{{ 'ASSISTANCE.VIEW_AS' | translate }}</span>
    <!-- Des boutons, pas des liens : `custom.scss` recolore tout `a:hover` en
         !important, ce qui rendait illisible la puce active survolée. -->
    <button *ngFor="let a of audiences" type="button" class="assist__chip" [class.is-active]="audience() === a"
            [attr.aria-pressed]="audience() === a" routerLink="/admin/help" [queryParams]="{ as: a }">
      <i [class]="audienceIcon(a)"></i> {{ audienceLabel(a) | translate }}
    </button>
  </div>
  <div class="viewas-note" *ngIf="isStaff && audience() !== 'Admin'" role="status">
    <i class="fa-solid fa-eye"></i>
    <span class="viewas-note__text">
      {{ (audience() === 'Professor' ? 'ASSISTANCE.VIEW_AS_NOTE_PROFESSOR' : 'ASSISTANCE.VIEW_AS_NOTE_STUDENT') | translate }}
    </span>
    <a class="viewas-back" routerLink="/admin/assistance">{{ 'ASSISTANCE.BACK_TO_MANAGE' | translate }}</a>
  </div>

  <!-- ── Loading ──────────────────────────────────────────────── -->
  <div *ngIf="loading()" class="assist__grid">
    <div class="assist__skel-card" *ngFor="let _ of [1,2,3,4]">
      <div class="assist__skel-thumb"></div>
      <div class="assist__skel-line assist__skel-line--title"></div>
      <div class="assist__skel-line assist__skel-line--link"></div>
    </div>
  </div>

  <!-- ── Error ────────────────────────────────────────────────── -->
  <app-error-state *ngIf="!loading() && errored()"
                   titleKey="ASSISTANCE.ERROR_TITLE"
                   messageKey="ASSISTANCE.LOAD_ERROR"
                   (retry)="reload()"></app-error-state>

  <!-- ── Modules ──────────────────────────────────────────────── -->
  <ng-container *ngIf="!loading() && !errored()">

    <div class="assist__search" *ngIf="modules().length > 0">
      <i class="fa-solid fa-magnifying-glass"></i>
      <input type="search"
             [value]="query()"
             (input)="query.set($any($event.target).value)"
             [placeholder]="'ASSISTANCE.SEARCH' | translate">
    </div>

    <ng-container *ngIf="current() as cur; else emptyTpl">

      <!-- Un onglet par module -->
      <nav class="mtabs" role="tablist">
        <button *ngFor="let entry of entries(); trackBy: trackEntry"
                type="button" role="tab" class="mtab"
                [class.is-active]="entry.module.id === cur.module.id"
                [attr.aria-selected]="entry.module.id === cur.module.id"
                (click)="selectedId.set(entry.module.id)">
          <span class="mtab__icon"><i class="fa-solid fa-layer-group"></i></span>
          <span class="mtab__txt">
            <span class="mtab__title">{{ entry.module.title }}</span>
            <span class="mtab__meta" *ngIf="summarize(entry.items) as s">
              <span class="meta-item" *ngIf="s.videos">{{ 'ASSISTANCE.COUNT_VIDEOS' | translate: { count: s.videos } }}</span>
              <span class="meta-item" *ngIf="s.documents">{{ 'ASSISTANCE.COUNT_DOCUMENTS' | translate: { count: s.documents } }}</span>
              <span class="meta-item" *ngIf="s.links">{{ 'ASSISTANCE.COUNT_LINKS' | translate: { count: s.links } }}</span>
            </span>
          </span>
        </button>
      </nav>

      <!-- La série du module ouvert -->
      <section class="assist__container" role="tabpanel">
        <div class="mpanel__head">
          <div class="mpanel__info">
            <div class="mpanel__kicker">{{ 'ASSISTANCE.MODULE_N' | translate: { n: position(cur.module) } }}</div>
            <h2 class="mpanel__title">{{ cur.module.title }}</h2>
            <p class="mpanel__desc" *ngIf="cur.module.description">{{ cur.module.description }}</p>
          </div>
          <button class="btn-start" type="button" (click)="onOpen(cur.items[0], cur.module)">
            <i class="fa-solid fa-play btn-start__icon"></i> {{ 'ASSISTANCE.START_MODULE' | translate }}
          </button>
        </div>

        <div class="assist__grid">
          <div class="assist__card" *ngFor="let r of cur.items; trackBy: trackResource"
               role="button" tabindex="0"
               (click)="onOpen(r, cur.module)" (keydown.enter)="onOpen(r, cur.module)">

            <div class="assist__thumb">
              <img *ngIf="r.previewUrl && kindOf(r) === 'image'"
                   [src]="r.previewUrl" [alt]="r.title" class="assist__thumb-img" loading="lazy">
              <div *ngIf="kindOf(r) === 'video'" class="assist__thumb-placeholder assist__thumb-placeholder--video">
                <i class="fa-solid fa-circle-play assist__play-icon"></i>
              </div>
              <div *ngIf="kindOf(r) !== 'video' && !(r.previewUrl && kindOf(r) === 'image')" class="assist__thumb-placeholder">
                <i [class]="kindIcon(r) + ' assist__file-type-icon'"></i>
              </div>
              <span class="assist__num">{{ r.displayOrder }}</span>
            </div>

            <h3 class="assist__card-title">{{ r.title }}</h3>

            <div class="assist__card-link">
              <i [class]="kindOf(r) === 'video' ? 'fa-solid fa-circle-play' : (kindOf(r) === 'link' ? 'fa-solid fa-link' : 'fa-solid fa-eye')"></i>
              <span>{{ (kindOf(r) === 'video' ? 'ASSISTANCE.WATCH_VIDEO' : 'ASSISTANCE.VIEW_RESOURCE') | translate }}</span>
            </div>
          </div>
        </div>
      </section>
    </ng-container>

    <ng-template #emptyTpl>
      <div class="assist__empty">
        <div class="assist__empty-icon">
          <i class="fa-solid" [class.fa-folder-open]="!query().trim()" [class.fa-magnifying-glass]="!!query().trim()"></i>
        </div>
        <h3 *ngIf="!query().trim()">{{ 'ASSISTANCE.EMPTY_TITLE' | translate }}</h3>
        <p>{{ (query().trim() ? 'ASSISTANCE.NO_MATCH' : 'ASSISTANCE.EMPTY_USER_DESC') | translate }}</p>
      </div>
    </ng-template>

  </ng-container>

  <!-- ── Lecteur ──────────────────────────────────────────────── -->
  <app-assistance-player *ngIf="playerResource() as r"
                         [resource]="r"
                         [series]="playerModule()?.resources ?? []"
                         [moduleTitle]="playerModule()?.title ?? ''"
                         (closed)="closePlayer()"
                         (navigate)="playerResource.set($event)"
                         (download)="onDownload($event)"></app-assistance-player>

</div>
